import { Injectable } from '@angular/core';
import { MEAPIService } from '../api/me-api.service';
import { VizAliasInfo, VizCard, VizCardEntity, VizCardPreferredForType, VizPayee } from './viz';
import { AsyncSubject, BehaviorSubject } from 'rxjs';
import { MEAPIResponse, MEEntityAPIResponseHandler, ResponseCode } from '../api/me-api-response';
import { Router } from '@angular/router';
import { ConfirmDialogConfig, DialogType } from '../../components/me-dialog/me-confirm/me-confirm.component';
import { TranslateUtilityService } from 'src/app/system/utility/translation-utility.service';
import { MeConfirmDialogService } from '../../components/me-dialog/me-confirm/me-confirm.service';
import { NavigationService } from 'src/app/system/utility/navigation.service';
import { Country } from '../../interfaces/country';

@Injectable({
  providedIn: 'root'
})
export class VizService {

  private readonly API_VIZ_ALIAS_INFO = MEAPIService.BASE_URL + '/customer/viz/alias/info';
  private readonly API_VIZ_CARDS = MEAPIService.BASE_URL + '/customer/viz/lastfour';
  private readonly API_VIZ_PAYEES = MEAPIService.BASE_URL + '/customer/viz/payees';

  public vizAliasInfo$: BehaviorSubject<VizAliasInfo | null> = new BehaviorSubject<VizAliasInfo | null>(null);
  public vizCards$: BehaviorSubject<VizCard[] | null> = new BehaviorSubject<VizCard[] | null>(null);
  public vizPayees$: BehaviorSubject<VizPayee[]> = new BehaviorSubject<VizPayee[]>([]);

  public country$ = new BehaviorSubject<Country | null>(null);

  constructor(
    private srvConfirmDialog: MeConfirmDialogService,
    private translate: TranslateUtilityService,
    private srvApi: MEAPIService, 
    private router: Router) {
  }

  public getVizAliasInfo(): AsyncSubject<VizAliasInfo> {
    let ret = new AsyncSubject<VizAliasInfo>();
    this.srvApi.Post(this.API_VIZ_ALIAS_INFO, {}, [new GetAliasInfoSuccessHandler(this.vizAliasInfo$, ret)]);
    return ret;
  }

  public getVizCards(): AsyncSubject<VizCard[]> {
    let ret = new AsyncSubject<VizCard[]>()
    this.srvApi.Post(this.API_VIZ_CARDS, {}, [new GetCardsSuccessHandler(this.vizCards$, ret)]);
    return ret;
  }

  public getPayeesList() {
    let ret = new AsyncSubject<VizPayee[]>()
    this.srvApi.Post(this.API_VIZ_PAYEES, {}, [new GetListSuccessHandler(this.vizPayees$, ret)]);
    return ret;
  }

  public maskCardNumber(cardNumber: string): string {
    return '....' + cardNumber;
  }

  public isVizModules(): boolean {
    return this.router.url.includes('viz');
  }

  public showContactDialog() {
		let confirmDialogConfig: ConfirmDialogConfig = {
			dialogType: DialogType.DELETE,
			title: this.translate.instant("VIZ.ALIAS_DISABLED"),
			message: this.translate.instant("VIZ.YOU_CANNOT_ACCESS_SEND_MONEY_BECAUSE_YOUR_ALIAS_IS_DISABLED") + ' ' + 
							 this.translate.instant("VIZ.PLEASE_CONTACT_THE_BRANCH"),
			cancelButtonText: this.translate.instant('BUTTON_OK')
		}
		this.srvConfirmDialog.showDialog(confirmDialogConfig).subscribe((isCancel) => {
			NavigationService.gotoDashboard(this.router);
		});
	}

	public showReviewDialog() {
		let confirmDialogConfig: ConfirmDialogConfig = {
			dialogType: DialogType.INFO,
			title: this.translate.instant("VIZ.ALIAS_DISABLED"),
			message: this.translate.instant("VIZ.YOU_CANNOT_ACCESS_SEND_MONEY_BECAUSE_YOUR_ALIAS_IS_DISABLED") + ' ' + 
							 this.translate.instant("VIZ.REVIEW_YOUR_ALIAS_STATUS_FIRST"),
			cancelButtonText: this.translate.instant('BUTTON_CANCEL'),
			confirmButtonText: this.translate.instant('BUTTON_REVIEW')
		}
		this.srvConfirmDialog.showDialog(confirmDialogConfig).subscribe((isConfirm) => {
			if (isConfirm) NavigationService.goto(this.router, '/settings/personal/viz-alias');
      else NavigationService.gotoDashboard(this.router);
		});
	}

  public resetSrv() {
    this.country$.next(null);
    this.vizAliasInfo$.next(null);
    this.vizCards$.next(null);
    this.vizPayees$.next([]);
  }
}

class GetAliasInfoSuccessHandler implements MEEntityAPIResponseHandler<VizAliasInfo> {
  public readonly code: ResponseCode = ResponseCode.SUCCESS;

  constructor(private vizAliasInfo$: BehaviorSubject<VizAliasInfo | null>, private subj: AsyncSubject<VizAliasInfo>) { }

  handle(response: MEAPIResponse<VizAliasInfo>): boolean {
    this.subj.next(response.entity);
    this.subj.complete();
    if (response.entity) {
      this.vizAliasInfo$.next(response.entity);
    }
    return true;
  }
}

class GetCardsSuccessHandler implements MEEntityAPIResponseHandler<any> {
  public readonly code: ResponseCode = ResponseCode.SUCCESS;

  constructor(private vizCards: BehaviorSubject<VizCard[] | null>, private subj: AsyncSubject<VizCard[]>) { }

  handle(response: MEAPIResponse<VizCardEntity>): boolean {
    if (response.entity.lastFourList && response.entity.lastFourList.length > 0) {
      (response.entity.lastFourList).forEach(card => {
        card.maskedCardNumber = '....' + card.lastFour;
        card.isPreferred = card.preferredFor?.includes(VizCardPreferredForType.RECEIVE) ?? false;
      })
      this.vizCards.next(response.entity.lastFourList);
      this.subj.next(response.entity.lastFourList);
      this.subj.complete();
    }
    return true;
  }
}

class GetListSuccessHandler implements MEEntityAPIResponseHandler<VizPayee[]> {
  public readonly code: ResponseCode = ResponseCode.SUCCESS;

  constructor(private vizPayees$: BehaviorSubject<VizPayee[]>, private subj: AsyncSubject<VizPayee[]>) { }

  handle(response: MEAPIResponse<VizPayee[]>): boolean {
    this.vizPayees$.next(response.entity);
    this.subj.next(response.entity);
    this.subj.complete();
    return true;
  }
}